<template>
	<div
		v-if="products.length > 0"
		:class="[
			'productOptions grid gap-x-05 gap-y-03',
			{
				'grid-cols-[1fr_auto_auto_auto]': canCalculateEventParameters,
				'grid-cols-[1fr_auto_auto]': !canCalculateEventParameters
			}
		]"
	>
		<div
			:class="[
				'headerRow font-caption-medium grid h-09 grid-cols-subgrid items-center bg-level-01 p-03',
				{
					'col-span-4': canCalculateEventParameters,
					'col-span-3': !canCalculateEventParameters
				}
			]"
		>
			{{ t('productsOptions.productName') }}

			<div class="whitespace-pre-line">
				{{ t('productsOptions.discountDepth') }}
			</div>

			<div v-if="canCalculateEventParameters">
				<div class="whitespace-nowrap">{{ t('productsOptions.advice') }}</div>
				<div class="cursor-pointer whitespace-nowrap text-primary" @click="showRecommendationInfoModal">
					{{ t('productsOptions.adviceHowItWorks') }}
				</div>
			</div>

			<div class="whitespace-pre-line">
				{{ t('productsOptions.pastEvents') }}
			</div>
		</div>

		<template v-if="products.length > 1 && !readonly && canCalculateEventParameters">
			<div class="col-start-3">
				<Tooltip :content="setAllRecommendationsTooltip">
					<Button size="xs" variant="quaternary" :disabled="!hasAnyRecommendationsToApply" @click="setAllRecommendations">{{
						t('productsOptions.setAll')
					}}</Button>
				</Tooltip>
			</div>

			<div>
				<!-- spacer -->
			</div>
		</template>

		<ProductOption
			v-for="sku in products"
			:key="sku.uniqueSkuId"
			:product="sku.humanName"
			:unique-sku-id="sku.uniqueSkuId"
			:discount-depth="sku.discountDepth"
			:show-indiebi-recommends="canCalculateEventParameters"
			:readonly="isReadonly"
			@update:discount-depth="(newDiscountDepth: number) => updateCustomDiscountDepth(sku.uniqueSkuId, newDiscountDepth)"
			@update:can-setup-product="handleCanSetupProduct($event, sku.uniqueSkuId)"
			@show-product-history="showProductHistory(sku)"
			@apply-to-all="(event) => applyDiscountToAllProducts(event)"
		></ProductOption>
	</div>

	<Modal v-if="isRecommendationInfoModalVisible" fullscreen>
		<template #title>
			<div class="font-h3-bold text-ink-60">{{ t('productsOptions.recommendationsModal.title') }}</div>
		</template>

		<div class="font-body-s mt-05 max-w-[40.75rem] whitespace-pre-line text-ink-87">
			<div>
				{{ t('productsOptions.recommendationsModal.body') }}
			</div>

			<div class="mt-06 flex justify-end">
				<Button size="s" variant="primary" @click="isRecommendationInfoModalVisible = false">{{
					t('productsOptions.recommendationsModal.close')
				}}</Button>
			</div>
		</div>
	</Modal>
</template>

<script setup lang="ts">
import { Button, isFinite as isFiniteNumber, Modal, Tooltip, useTranslation } from '@bluebrick/saas-ui'
import { storeToRefs } from 'pinia'
import { computed, ref, toRefs, watch } from 'vue'
import ProductOption from '@/features/eventDetails/components/ProductOption.vue'
import { isDepthRecommendationAvailable } from '@/features/eventDetails/eventDetailsUtils'
import { useEventDetailsStore } from '@/features/eventDetails/useEventDetailsStore'
import { useEventsStore } from '@/features/events/useEventsStore'
import { useSidebarStore } from '@/features/sidePanel/useSidebarStore'
import { useTelemetry } from '@/features/telemetry/useTelemetry'
import { type SkuOption, type StoreData } from '@/types'

const { isReadonly, products, recommendedDiscountDepth, canCalculateEventParameters, store, startDate, sourceEvent } =
	storeToRefs(useEventDetailsStore())
const { updateCustomDiscountDepth } = useEventDetailsStore()
const { storeEvents } = storeToRefs(useEventsStore())
const { showProductHistory } = useSidebarStore()
const { trackEvent } = useTelemetry()
const { t } = useTranslation()

const props = defineProps<{
	readonly?: boolean
}>()

const { readonly } = toRefs(props)

const emit = defineEmits<{
	invalidProducts: [skuIds: Set<string>]
}>()

const productsWithIssues = ref<Set<string>>(new Set())

function hasValidRecommendation(product: SkuOption) {
	const recommendation = recommendedDiscountDepth.value.get(product.uniqueSkuId)
	const current = product.discountDepth

	return (
		store.value &&
		startDate.value &&
		isDepthRecommendationAvailable(
			storeEvents.value.get(store.value.id) as StoreData,
			product.humanName,
			startDate.value,
			sourceEvent.value?.id ?? null
		) &&
		isFiniteNumber(recommendation) &&
		isFiniteNumber(current) &&
		recommendation !== current
	)
}

const hasAnyRecommendationsToApply = computed(() => products.value?.some(hasValidRecommendation))
const isValidRecommendationAvailableForAllProducts = computed(() => products.value?.every(hasValidRecommendation))

const isRecommendationInfoModalVisible = ref(false)

const setAllRecommendationsTooltip = computed(() => {
	if (isValidRecommendationAvailableForAllProducts.value) {
		return t('productsOptions.setAllTooltip')
	}

	if (hasAnyRecommendationsToApply.value) {
		return t('productsOptions.setSomeTooltip')
	}

	return t('productsOptions.setNoneTooltip')
})

function showRecommendationInfoModal() {
	trackEvent('user', 'Show recommendation info modal')

	isRecommendationInfoModalVisible.value = true
}

function handleCanSetupProduct(canSetup: boolean, uniqueSkuId: string) {
	if (canSetup) {
		productsWithIssues.value.delete(uniqueSkuId)
	} else {
		productsWithIssues.value.add(uniqueSkuId)
	}

	emit('invalidProducts', productsWithIssues.value)
}

function setAllRecommendations() {
	trackEvent('user', 'Apply all recommended depths')

	products.value?.forEach((product) => {
		const recommendation = recommendedDiscountDepth.value.get(product.uniqueSkuId)

		if (hasValidRecommendation(product) && isFiniteNumber(recommendation)) {
			updateCustomDiscountDepth(product.uniqueSkuId, recommendation)
		}
	})
}

function applyDiscountToAllProducts(discount: number) {
	if (!isFinite(discount) || discount < 1 || discount > 99) {
		return
	}

	products.value?.forEach((product) => {
		updateCustomDiscountDepth(product.uniqueSkuId, discount)
	})
}

function updateValidationState() {
	// Remove products which no longer participate in the event
	for (const skuId of productsWithIssues.value) {
		if (!products.value.some(({ uniqueSkuId }) => uniqueSkuId === skuId)) {
			productsWithIssues.value.delete(skuId)

			emit('invalidProducts', productsWithIssues.value)
		}
	}
}

watch(products, updateValidationState)
</script>
